<template>
  <div class="alipay-auth-launch">
    <div class="launch-container">
      <div class="launch-header">
        <img class="logo-img" src="@assets/images/alipay.png" alt="支付宝" />
        <h1 class="title">正在跳转授权</h1>
        <p class="subtitle">{{ headerTip }}</p>
      </div>

      <div class="launch-body">
        <div v-if="loading" class="loading-box">
          <div class="loading-spinner"></div>
          <p class="loading-text">正在准备授权链接...</p>
        </div>

        <template v-else-if="isInWechat">
          <p class="desc">请点击右上角「...」，选择「在浏览器打开」以完成支付宝授权。</p>
          <div class="wechat-notice">
            <span class="notice-icon">!</span>
            <p>授权完成后返回微信，系统将自动检测授权结果</p>
          </div>
        </template>
      </div>
    </div>

    <browser-mask
      :visible.sync="showMask"
      title="授权提示"
      message='请点击右上角<b>"..."</b>，选择<b>"在浏览器打开"</b>以完成支付宝授权'
    />
  </div>
</template>

<script>
import BrowserMask from "@components/BrowserMask";
import { getAlipayAuthUrl, getAlipayAuthLaunchUrl, getAlipayAuthStatus } from "@api/user";
import { isWeixin } from "@utils";

const AUTH_POLL_INTERVAL = 3000;
const AUTH_POLL_DELAY = 20000;

export default {
  name: "AlipayAuthLaunch",
  components: {
    BrowserMask
  },
  data() {
    return {
      isInWechat: false,
      loading: true,
      showMask: false,
      pollTimer: null,
      pollDelayTimer: null,
      pollChecking: false,
      pollingActive: false
    };
  },
  computed: {
    headerTip() {
      if (this.loading) {
        return "请稍候";
      }
      return this.isInWechat ? "请在浏览器中打开" : "即将前往支付宝";
    }
  },
  mounted() {
    this.isInWechat = isWeixin();
    this.initLaunch();
  },
  beforeDestroy() {
    this.pollingActive = false;
    this.stopAuthPolling();
  },
  methods: {
    initLaunch() {
      if (this.isInWechat) {
        this.loading = false;
        this.showMask = true;
        this.startAuthPolling();
        return;
      }
      this.redirectToAlipay();
    },

    redirectToAlipay() {
      this.loading = true;
      const state = this.$route.query.state;
      const request = state
        ? getAlipayAuthLaunchUrl({ state })
        : getAlipayAuthUrl();

      request
        .then(res => {
          const url = res.data && res.data.url;
          if (!url) {
            return Promise.reject({ msg: "未获取到授权跳转链接" });
          }
          window.location.replace(url);
        })
        .catch(err => {
          this.loading = false;
          this.$dialog.error(err.msg || "发起授权失败", () => {
            this.$router.replace("/user/alipay/auth");
          });
        });
    },

    startAuthPolling() {
      this.stopAuthPolling();
      this.pollingActive = true;
      this.pollDelayTimer = setTimeout(() => {
        this.pollDelayTimer = null;
        if (!this.pollingActive) {
          return;
        }
        this.pollAuthStatus();
        this.pollTimer = setInterval(() => {
          this.pollAuthStatus();
        }, AUTH_POLL_INTERVAL);
      }, AUTH_POLL_DELAY);
    },

    stopAuthPolling() {
      this.pollingActive = false;
      if (this.pollDelayTimer) {
        clearTimeout(this.pollDelayTimer);
        this.pollDelayTimer = null;
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollChecking = false;
    },

    pollAuthStatus() {
      if (!this.pollingActive || this.pollChecking) {
        return;
      }
      this.pollChecking = true;
      getAlipayAuthStatus()
        .then(res => {
          if (!this.pollingActive) {
            return;
          }
          if (res.data && res.data.authorized) {
            this.handleAuthSuccess();
          }
        })
        .catch(() => {})
        .finally(() => {
          this.pollChecking = false;
        });
    },

    handleAuthSuccess() {
      this.stopAuthPolling();
      this.showMask = false;
      this.$dialog.alert({
        title: "提示",
        mes: "您已经完成授权！"
      }, () => {
        this.$router.go(-2);
      });
    }
  }
};
</script>

<style scoped>
.alipay-auth-launch {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 10vh 16px 24px;
  background: linear-gradient(180deg, #e8f3ff 0%, #f5f7fa 42%, #f5f7fa 100%);
}

.launch-container {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(22, 119, 255, 0.12);
}

.launch-header {
  padding: 32px 24px 28px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
}

.logo-img {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: block;
  object-fit: contain;
}

.title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
}

.subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.92;
}

.launch-body {
  padding: 28px 24px 32px;
}

.loading-box {
  text-align: center;
}

.loading-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto 16px;
  border: 3px solid #e6f0ff;
  border-top-color: #1677ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin: 0;
  font-size: 15px;
  color: #666;
}

.desc {
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
  color: #333;
  text-align: center;
}

.wechat-notice {
  display: flex;
  align-items: flex-start;
  margin-top: 20px;
  padding: 12px 14px;
  background: #fff8e6;
  border: 1px solid #ffe7a3;
  border-radius: 10px;
}

.notice-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: 10px;
  border-radius: 50%;
  background: #faad14;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.wechat-notice p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #8c6d1f;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
